/**
 * The Schaak training engine (docs/SCHAAK.md §3.1): spaced repetition per
 * puzzle, a puzzle rating, theme unlocking, choosing the next puzzle, and the
 * Woodpecker cycles. Pure — no clock, no storage, no randomness it was not
 * handed. The Leitner scheme follows Morse's, copied rather than imported:
 * modules stay code-independent.
 */
import { SCHAAK_THEMES, THEME_MASTERY } from './themes.js';
/**
 * Minutes until the next review, per box. A miss comes back after ten minutes —
 * still in today's session — then a day, three, a week, three weeks, two months.
 */
export const CARD_INTERVALS_MIN = [10, 24 * 60, 3 * 24 * 60, 7 * 24 * 60, 21 * 24 * 60, 60 * 24 * 60];
export const MAX_CARD_BOX = CARD_INTERVALS_MIN.length - 1;
/**
 * Where a puzzle solved at the first try starts. Easy patterns are still worth
 * seeing again ("mass repetition of simple tactics"), but not tomorrow.
 */
export const FIRST_SOLVE_BOX = 2;
const addMinutes = (now, min) => new Date(now.getTime() + min * 60_000).toISOString();
export function recordCard(card, kind, ref, theme, solved, now) {
    const box = !card ? (solved ? FIRST_SOLVE_BOX : 0) : solved ? Math.min(MAX_CARD_BOX, card.box + 1) : 0;
    return {
        kind,
        ref,
        theme: card?.theme ?? theme,
        box,
        due_at: addMinutes(now, CARD_INTERVALS_MIN[box]),
        attempts: (card?.attempts ?? 0) + 1,
        solved: (card?.solved ?? 0) + (solved ? 1 : 0),
        failed: (card?.failed ?? 0) + (solved ? 0 : 1),
        first_result: card?.first_result ?? (solved ? 'solved' : 'failed'),
        last_at: now.toISOString(),
        reason: card?.reason ?? null,
    };
}
/** Cards due now, most overdue first. */
export function dueCards(cards, now) {
    const t = now.toISOString();
    return Object.values(cards)
        .filter((c) => !!c && !!c.due_at && c.due_at <= t)
        .sort((a, b) => (a.due_at < b.due_at ? -1 : 1));
}
/* ---------------- rating ---------------- */
export const START_RATING = 800;
/**
 * Elo against the puzzle's own rating, first attempts only (a review you have
 * seen before says little about strength — Lichess counts the same way). A big
 * K while the rating is young, so it finds his level within a few sessions.
 */
export function updateRating(rating, games, puzzleRating, solved) {
    const k = games < 20 ? 40 : games < 60 ? 24 : 16;
    const expected = 1 / (1 + 10 ** ((puzzleRating - rating) / 400));
    return Math.round(Math.max(100, rating + k * ((solved ? 1 : 0) - expected)));
}
/* ---------------- themes ---------------- */
export function themeSolves(cards, theme) {
    let n = 0;
    for (const c of Object.values(cards))
        if (c?.kind === 'puzzle' && c.theme === theme && c.first_result === 'solved')
            n++;
    return n;
}
/** Theme keys open to practise: the first always, each next one after the one before is mastered. */
export function unlockedThemes(cards) {
    const out = [];
    for (const [i, t] of SCHAAK_THEMES.entries()) {
        if (i > 0 && themeSolves(cards, SCHAAK_THEMES[i - 1].key) < THEME_MASTERY)
            break;
        out.push(t.key);
    }
    return out;
}
/**
 * The next new puzzle: unseen, in one of `themes`, near his rating. Slightly
 * below it on purpose — the advice for improvers is many solvable patterns, not
 * a fight at the edge. Picked at random among the closest few so a session does
 * not march through the list in one fixed order.
 */
export const NEW_PUZZLE_OFFSET = 50;
export function pickNewPuzzle(puzzles, cards, themes, rating, rand, exclude = new Set()) {
    const want = new Set(themes);
    const target = rating - NEW_PUZZLE_OFFSET;
    const pool = puzzles
        .filter((p) => want.has(p.theme) && !cards[p.id] && !exclude.has(p.id))
        .sort((a, b) => Math.abs(a.rating - target) - Math.abs(b.rating - target))
        .slice(0, 12);
    if (pool.length === 0)
        return null;
    return pool[Math.min(pool.length - 1, Math.floor(rand() * pool.length))];
}
/** Puzzles attempted today (local day), counted by each card's latest attempt. Drills are not puzzles. */
export function attemptedToday(cards, now) {
    const day = now.toDateString();
    let n = 0;
    for (const c of Object.values(cards)) {
        if (c && c.kind !== 'drill' && c.last_at && new Date(c.last_at).toDateString() === day)
            n++;
    }
    return n;
}
/** The daily target — fifteen-ish minutes. */
export const DAILY_GOAL = 15;
/* ---------------- Woodpecker ---------------- */
/**
 * Smith & Tikkanen's Woodpecker Method: one fixed set, solved again and again,
 * each cycle in about half the time of the one before. These are the target
 * days per cycle; the seventh cycle is the last.
 */
export const WOODPECKER_TARGET_DAYS = [14, 7, 4, 2, 1, 1, 1];
export const WOODPECKER_CYCLES = WOODPECKER_TARGET_DAYS.length;
export function startCycle(set_no, cycle, now) {
    return { set_no, cycle, started_at: now.toISOString(), finished_at: null, position: 0, correct: 0, seconds: 0 };
}
export function recordWoodpecker(c, solved, seconds, setSize, now) {
    const position = c.position + 1;
    return {
        ...c,
        position,
        correct: c.correct + (solved ? 1 : 0),
        seconds: c.seconds + Math.max(0, Math.round(seconds)),
        finished_at: position >= setSize ? now.toISOString() : null,
    };
}
/** The cycle to work on: the latest unfinished one, else a new one after the latest. */
export function currentCycle(cycles, set_no, now) {
    const mine = cycles.filter((c) => c.set_no === set_no).sort((a, b) => a.cycle - b.cycle);
    const last = mine[mine.length - 1];
    if (!last)
        return startCycle(set_no, 1, now);
    if (!last.finished_at)
        return last;
    if (last.cycle >= WOODPECKER_CYCLES)
        return null;
    return startCycle(set_no, last.cycle + 1, now);
}
/** Days left of a cycle's target window (negative once over). */
export function cycleDaysLeft(c, now) {
    const days = WOODPECKER_TARGET_DAYS[Math.min(c.cycle, WOODPECKER_CYCLES) - 1];
    const end = new Date(c.started_at).getTime() + days * 86_400_000;
    return Math.ceil((end - now.getTime()) / 86_400_000);
}
/* ---------------- ids ---------------- */
function fnv(s, seed) {
    let h = (0x811c9dc5 ^ seed) >>> 0;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}
/**
 * A row id **derived, not random** (the `morseLetterId` lesson): two offline
 * phones meeting the same puzzle must write the same row, or LWW keeps two.
 */
export function schaakId(...parts) {
    const key = `schaak:${parts.join(':')}`;
    const hex = [1, 2, 3, 4].map((seed) => fnv(key, seed).toString(16).padStart(8, '0')).join('');
    return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        '4' + hex.slice(13, 16),
        ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20),
        hex.slice(20, 32),
    ].join('-');
}
//# sourceMappingURL=training.js.map